import { Grid } from '@mui/material'
import { KPhoneField } from 'components/core/KPhoneField'
import { KTextField } from 'components/core/KTextField'
import { useTranslation } from 'react-i18next'
import { SubmitOrderSection } from './SubmitOrderSection'
import { SubmitOrderSectionProps } from './SubmitOrderSectionProps'
import { useOrdering, useCurrentUser } from 'hooks'
import { useEffect, useRef } from 'react'
import { formatPhoneNumber } from 'utils/phoneUtils'
import {
  combine,
  required,
  maxLength,
  email as emailValidation,
  phone as phoneValidation,
} from 'utils/formValidation'

export const OrderCustomerInfo = ({
  form: {
    register,
    getValues,
    setValue,
    formState: { errors },
  },
}: SubmitOrderSectionProps) => {
  const { t } = useTranslation()
  const { saveOrderProps, order } = useOrdering()
  const { currentUser } = useCurrentUser()
  const hasAutoFilled = useRef(false)

  useEffect(() => {
    if (currentUser && !hasAutoFilled.current && order?.id) {
      const firstName = getValues('customerInfo.firstName')
      const lastName = getValues('customerInfo.lastName')
      const email = getValues('customerInfo.email')
      const phone = getValues('customerInfo.phone')

      if (!firstName && currentUser.firstName) {
        setValue('customerInfo.firstName', currentUser.firstName)
      }

      if (!lastName && currentUser.lastName) {
        setValue('customerInfo.lastName', currentUser.lastName)
      }

      if (!email && currentUser.email) {
        setValue('customerInfo.email', currentUser.email)
      }

      if (
        phone === currentUser.phoneNumber ||
        (!phone && currentUser.phoneNumber)
      ) {
        setValue(
          'customerInfo.phone',
          formatPhoneNumber(currentUser.phoneNumber),
        )
      }

      hasAutoFilled.current = true
    }
  }, [currentUser, getValues, setValue, saveOrderProps, order])

  if (!register) {
    return null
  }

  return (
    <SubmitOrderSection label={t('Customer info')}>
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6}>
          <KTextField
            required
            label={t('First name')}
            inputProps={{ maxLength: 50 }}
            helperText={errors?.customerInfo?.firstName?.message}
            error={!!errors?.customerInfo?.firstName}
            {...register(
              'customerInfo.firstName',
              combine(
                required(t, 'First name'),
                maxLength(t, 'First name', 50),
              ),
            )}
            onChange={(e) => {
              saveOrderProps({ customer_first_name: e.target.value })
              setValue('customerInfo.firstName', e.target.value)
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <KTextField
            required
            label={t('Last name')}
            inputProps={{ maxLength: 50 }}
            helperText={errors?.customerInfo?.lastName?.message}
            error={!!errors?.customerInfo?.lastName}
            {...register(
              'customerInfo.lastName',
              combine(required(t, 'Last name'), maxLength(t, 'Last name', 50)),
            )}
            onChange={(e) => {
              saveOrderProps({ customer_last_name: e.target.value })
              setValue('customerInfo.lastName', e.target.value)
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <KTextField
            required
            label={t('Email')}
            placeholder="john.doe@gmail.com"
            inputProps={{ maxLength: 50 }}
            helperText={
              errors?.customerInfo?.email?.message ??
              t('Order receipts/updates will be sent here.')
            }
            error={!!errors?.customerInfo?.email}
            {...register(
              'customerInfo.email',
              combine(
                required(t, 'Email'),
                emailValidation(t),
                maxLength(t, 'Email', 50),
              ),
            )}
            onChange={(e) => {
              saveOrderProps({ customer_email: e.target.value })
              setValue('customerInfo.email', e.target.value)
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <KPhoneField
            label={t('Phone')}
            required
            helperText={
              errors?.customerInfo?.phone?.message ??
              t('Order receipts/updates will be sent here.')
            }
            error={!!errors?.customerInfo?.phone}
            value={getValues('customerInfo.phone') ?? ''}
            {...register(
              'customerInfo.phone',
              combine(required(t, 'Phone'), phoneValidation(t)),
            )}
            onChange={(e) => {
              saveOrderProps({ customer_phone: e.target.value })
              setValue('customerInfo.phone', e.target.value)
            }}
          />
        </Grid>
      </Grid>
    </SubmitOrderSection>
  )
}
